/**
 * Buildings API — all backend calls for the Buildings tab.
 *
 * Each function maps directly to a V1 endpoint / stored procedure
 * but uses the new V2 api client for cleaner code.
 *
 * Endpoints & payloads are identical to V1 for full backend compatibility.
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all buildings (with their areas) for a campus.
 * V1 procedure: getbuildingsandareas
 *
 * @param {number} campusId
 * @param {boolean} [readOnly=true] — false on first load to bust cache
 * @returns {Promise<Array>}
 */
export function fetchBuildings(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'getbuildingsandareas',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    payload: { campus_id: campusId },
    readOnly,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Add a new building.
 * V1 TransType: building_add
 *
 * @param {object} data — Building form data
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addBuilding(data, session) {
  const payload = [{
    campus_id: session.campusId,
    building_name: data.building_name,
    street_address: data.street_address || '',
    city: data.city || '',
    state: data.state || '',
    zipcode: data.zipcode || '',
    country: data.country || '',
    area_ids: data.area_ids || [],
  }];

  return apiTransaction({
    transType: 'building_add',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Edit an existing building.
 * V1 TransType: building_edit
 *
 * @param {object} data — Updated building data
 * @param {object} oldData — Original building data (for change tracking)
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function editBuilding(data, oldData, session) {
  const payload = [{
    building_id: parseInt(data.building_id),
    campus_id: session.campusId,
    building_name: data.building_name,
    street_address: data.street_address || '',
    city: data.city || '',
    state: data.state || '',
    zipcode: data.zipcode || '',
    country: data.country || '',
    area_ids: data.area_ids || [],
  }];

  const oldPayload = [{
    building_name: oldData.building_name,
    street_address: oldData.street_address || '',
    city: oldData.city || '',
    state: oldData.state || '',
    zipcode: oldData.zipcode || '',
    country: oldData.country || '',
    area_ids: oldData.area_ids || [],
  }];

  return apiTransaction({
    transType: 'building_edit',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete one or more buildings.
 * V1 TransType: building_delete
 *
 * @param {Array<number>} buildingIds
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteBuildings(buildingIds, session) {
  const payload = buildingIds.map((id) => ({
    building_id: parseInt(id),
  }));

  return apiTransaction({
    transType: 'building_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
